import { ControlOutlined, DownOutlined, UpOutlined } from '@ant-design/icons'
import { Button } from '@lmc/ui-kit'
import { NAMESPACE } from 'locale'
import { reportsConstants } from 'locale/team'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

type TeamFiltersButtonProps = {
  // Applied-filter count. Rendered as a badge only when `showCount` is set —
  // the group dashboard shows it; the single-site popover has no indicator per
  // the design (CM-3581).
  activeCount?: number
  showCount?: boolean
  onClick?: () => void
  // When provided, renders an up/down chevron reflecting popover open state
  // (single-site popover). Omit for the group side panel (no chevron).
  open?: boolean
  testId?: string
}

// No left margin on the trailing elements: the ui-kit Button already spaces its
// children with an 8px flex gap, so an extra margin double-gaps them off the
// label (CM-3581 review).
const StyledChevron = styled.span`
  display: inline-flex;
  font-size: 12px;
`

// Count badge — the applied-filter count in a circle, matching the Figma
// "Log Indicator": an OUTLINED circle (pale #F0FCFF fill, brand-blue 1px ring),
// blue number — not a solid fill.
const StyledCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background-color: #f0fcff;
  border: 1px solid ${({ theme }) => theme.color.status.primary};
  color: ${({ theme }) => theme.color.status.primary};
  font-size: 14px;
  line-height: 1;
  font-weight: 500;
`

const TeamFiltersButton: React.FC<TeamFiltersButtonProps> = ({
  activeCount = 0,
  showCount = false,
  onClick,
  open,
  testId
}) => {
  const { t } = useTranslation(NAMESPACE.TEAM)

  return (
    <Button
      variant="default"
      icon={<ControlOutlined />}
      onClick={onClick}
      data-testid={testId}
    >
      {t(reportsConstants.TEAM_LIST_FILTERS)}
      {showCount && activeCount > 0 && (
        <StyledCount data-testid={testId ? `${testId}-indicator` : undefined}>
          {activeCount}
        </StyledCount>
      )}
      {open !== undefined && (
        <StyledChevron>
          {open ? <UpOutlined /> : <DownOutlined />}
        </StyledChevron>
      )}
    </Button>
  )
}

export default TeamFiltersButton
